import type { SdkLogMetadata } from '../types/shared';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'success' | 'perf' | 'analytics';

let loggingEnabled = false;

// CSS styles for console log level badges
const BADGE_STYLES: Record<LogLevel, string> = {
  debug: 'background: #7f8c8d; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  info: 'background: #2980b9; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  warn: 'background: #f1c40f; color: #000; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  error: 'background: #e74c3c; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  success: 'background: #2ecc71; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  perf: 'background: #9b59b6; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
  analytics: 'background: #e20074; color: #fff; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
};

/**
 * Writes formatted badges and log details into the console if logging is enabled.
 * Note: analytics level logs are always printed regardless of debug settings.
 *
 * @param level Logging categorization level (debug, info, warn, error, success, analytics).
 * @param message Description message text.
 * @param meta Optional auxiliary metadata/payload.
 */
function writeLog(level: LogLevel, message: string, meta?: SdkLogMetadata) {
  if (!loggingEnabled && level !== 'analytics') return;

  const timestamp = new Date().toLocaleTimeString();
  const badge = `%c[SDK:${level.toUpperCase()}]`;
  const style = BADGE_STYLES[level];

  if (meta !== undefined) {
    console.log(`[${timestamp}] ${badge}`, style, message, meta);
  } else {
    console.log(`[${timestamp}] ${badge}`, style, message);
  }
}

/**
 * A central namespace containing logging helpers for debugging, warnings, success,
 * and API monitoring. Output logs are formatted with custom browser styles.
 */
export const sdkLogger = {
  setEnabled: (val: boolean) => {
    loggingEnabled = val;
  },
  isEnabled: () => {
    return loggingEnabled;
  },
  debug: (msg: string, meta?: SdkLogMetadata) => writeLog('debug', msg, meta),
  info: (msg: string, meta?: SdkLogMetadata) => writeLog('info', msg, meta),
  warn: (msg: string, meta?: SdkLogMetadata) => writeLog('warn', msg, meta),
  error: (msg: string, meta?: SdkLogMetadata) => writeLog('error', msg, meta),
  success: (msg: string, meta?: SdkLogMetadata) => writeLog('success', msg, meta),
  analytics: (msg: string, meta?: SdkLogMetadata) => writeLog('analytics', msg, meta),
  perf: (metric: {
    apiName: string;
    durationMs: number;
    status: number | string;
    url: string;
    requestSize?: string;
    responseSize?: string;
    payloadSize?: string;
    success: boolean;
  }) => {
    writeLog(
      'perf',
      `${metric.apiName} completed in ${metric.durationMs.toFixed(2)}ms | Status: ${metric.status}`,
      metric
    );
  },
};
